import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { fetchVersions, fetchVersion, clearCurrentVersion } from "../Redux/feasibility";
import { ContentModal } from "../FeasibilityEditor/VersionContent";
import { DiffModal } from "../FeasibilityEditor/VersionDiff";
import { sfqApi } from "../../../api/feasibility";

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 10px;
  border-bottom: 2px solid #dee2e6;
  color: #6c757d;
  font-weight: 600;
  font-size: 12px;
`;

const Td = styled.td`
  padding: 8px 10px;
  border-bottom: 1px solid #f1f3f5;
  color: #212529;
`;

const VerBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  background: #e7f1ff;
  color: #0d6efd;
  font-weight: 600;
  font-size: 12px;
`;

const ViewBtn = styled.button`
  padding: 3px 12px;
  border-radius: 4px;
  border: 1px solid #0d6efd;
  background: #fff;
  color: #0d6efd;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: #e7f1ff; }
  &:disabled {
    border-color: #dee2e6;
    color: #adb5bd;
    cursor: not-allowed;
    background: #fff;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  justify-content: flex-end;
`;

const Empty = styled.div`
  padding: 32px 0;
  text-align: center;
  color: #adb5bd;
  font-size: 13px;
`;

function fmtDateTime(val) {
  if (!val) return "-";
  return new Date(val).toLocaleString("ko-KR", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function VersionHistory({ sfqId }) {
  const dispatch = useDispatch();
  const versions = useSelector((s) => s.feasibility.versions);
  const loading  = useSelector((s) => s.feasibility.versionsLoading);
  const version  = useSelector((s) => s.feasibility.currentVersion);
  const [open, setOpen] = useState(false);
  // The diff is only ever read here, so it stays in local state rather than the slice
  // (which keeps a single currentVersion slot, unrelated to comparing two of them).
  const [diff, setDiff] = useState(null);

  useEffect(() => {
    if (sfqId) dispatch(fetchVersions(sfqId));
  }, [sfqId, dispatch]);

  function openVersion(versionId) {
    setOpen(true);
    dispatch(fetchVersion({ id: sfqId, versionId }));
  }

  function close() {
    setOpen(false);
    dispatch(clearCurrentVersion());
  }

  async function openDiff(v) {
    setDiff({ versionNo: v.versionNo, loading: true, data: null, error: null });
    try {
      const res = await sfqApi.getVersionDiff(sfqId, v.id);
      setDiff({ versionNo: v.versionNo, loading: false, data: res.data.data, error: null });
    } catch (err) {
      setDiff({
        versionNo: v.versionNo,
        loading: false,
        data: null,
        error: err?.response?.data?.error?.message || err?.message ||
               "변경 내역을 불러오지 못했습니다.",
      });
    }
  }

  if (loading) return <Empty>Loading...</Empty>;
  if (!versions.length) {
    return <Empty>아직 발행된 버전이 없습니다. Study가 Active 되면 첫 버전이 생성됩니다.</Empty>;
  }

  return (
    <>
      <Table>
        <thead>
          <tr>
            <Th style={{ width: 90 }}>Version</Th>
            <Th>Created</Th>
            <Th>Created By</Th>
            <Th>First Sent</Th>
            <Th style={{ width: 90 }}>Sites</Th>
            <Th style={{ width: 190 }} />
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr key={v.id}>
              <Td><VerBadge>V{v.versionNo}</VerBadge></Td>
              <Td>{fmtDateTime(v.createdAt)}</Td>
              <Td>{v.createdByName || v.createdBy || "-"}</Td>
              <Td>{fmtDateTime(v.sentAt)}</Td>
              <Td>{v.invitationCount}</Td>
              <Td>
                <Actions>
                  <ViewBtn onClick={() => openVersion(v.id)}>View</ViewBtn>
                  {/* V1 has no predecessor to compare against. */}
                  <ViewBtn
                    disabled={v.versionNo <= 1}
                    title={v.versionNo <= 1 ? "첫 번째 버전입니다." : `V${v.versionNo - 1}과 비교`}
                    onClick={() => openDiff(v)}
                  >
                    변경 내역
                  </ViewBtn>
                </Actions>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>

      {open && (
        <ContentModal
          title={version ? `Version ${version.versionNo}` : "Loading..."}
          loading={!version}
          narrativeHtml={version?.narrativeHtml}
          questions={version?.questions}
          onClose={close}
        />
      )}

      {diff && (
        <DiffModal
          title={`V${diff.versionNo - 1} → V${diff.versionNo} 변경 내역`}
          subtitle={`V${diff.versionNo}에서 이전 버전(V${diff.versionNo - 1}) 대비 달라진 부분입니다.`}
          diff={diff.data}
          loading={diff.loading}
          error={diff.error}
          onClose={() => setDiff(null)}
        />
      )}
    </>
  );
}
